import React from 'react';
import { ArrowRight, FlaskConical, Layers, ShieldCheck, Workflow } from 'lucide-react';
import useScrollAnimation from '../hooks/useScrollAnimation';
import { labCapacity, labProducts, labStats } from '../data/lab';
import { useModal } from '../context/ModalContext';

const capacityIcons = [FlaskConical, ShieldCheck, Workflow, Layers];

const Lab = () => {
  const { openDemoModal } = useModal();
  const [sectionRef, isVisible] = useScrollAnimation({ threshold: 0.1 });

  return (
    <section className="relative py-24 md:py-28 bg-[--bg-primary] min-h-[70vh]">
      <div className="absolute top-0 left-0 right-0 section-divider" />

      <div ref={sectionRef} className="max-w-[1500px] mx-auto px-4 sm:px-6">
        <div
          className={`max-w-5xl mb-14 transition-all duration-700 ease-out ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
          }`}
        >
          <p className="section-eyebrow mb-4">Lab</p>
          <h1 className="text-[clamp(2rem,4.2vw,4rem)] font-semibold text-[--text-primary] leading-[1.02]">
            Synentia Lab portfolio of
            <br />
            <span className="accent-wash">production-grade AI applications.</span>
          </h1>
          <p className="mt-6 text-lg text-[--text-secondary] max-w-4xl">
            The Lab builds, tests, and deploys AI products for real business environments. These
            are not demo prototypes. Each build is designed for enterprise reliability, security,
            compliance, and measurable operating impact.
          </p>
        </div>

        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4 mb-16">
          {labProducts.map((product, index) => (
            <article
              key={product.id}
              className={`rounded-2xl soft-card p-6 border border-[--border] hover:border-[--border-hover] transition-all duration-500 ${
                isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
              }`}
              style={{ transitionDelay: `${120 + index * 60}ms` }}
            >
              <div className="flex items-center justify-between gap-4 mb-4">
                <span className="text-[10px] font-mono uppercase tracking-[0.16em] text-[--text-muted]">
                  {product.id}
                </span>
                <span className="text-[10px] font-mono uppercase tracking-[0.15em] px-2.5 py-1 rounded-full bg-[--bg-tertiary] text-[--accent] border border-[--border]">
                  {product.status}
                </span>
              </div>

              <p className="text-[11px] font-mono uppercase tracking-[0.16em] text-[--text-muted]">
                {product.category}
              </p>
              <h2 className="mt-2 text-[1.3rem] font-semibold text-[--text-primary] leading-tight">
                {product.name}
              </h2>
              <p className="mt-3 text-sm text-[--text-secondary] leading-relaxed">{product.summary}</p>

              <div className="mt-5 pt-5 border-t border-[--border]">
                <p className="text-[11px] font-mono uppercase tracking-[0.16em] text-[--text-muted] mb-2">
                  Delivered Impact
                </p>
                <p className="text-sm text-[--text-primary]">{product.impact}</p>
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                {product.readiness.map((item) => (
                  <span
                    key={`${product.id}-${item}`}
                    className="text-[10px] font-mono uppercase tracking-[0.12em] px-2 py-1 rounded-md border border-[--border] text-[--text-secondary]"
                  >
                    {item}
                  </span>
                ))}
              </div>
            </article>
          ))}
        </div>

        <div className="grid lg:grid-cols-[1.05fr_0.95fr] gap-5 mb-16">
          <div
            className={`soft-card rounded-3xl p-8 md:p-10 transition-all duration-700 ease-out delay-150 ${
              isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}
          >
            <p className="section-eyebrow mb-4">Lab Capacity</p>
            <h3 className="text-[clamp(1.6rem,3vw,2.5rem)] font-semibold text-[--text-primary] leading-[1.08]">
              Capacity to develop
              <br />
              <span className="accent-wash">industry-ready applications.</span>
            </h3>
            <p className="mt-5 text-[--text-secondary] leading-relaxed max-w-2xl">
              The Synentia Lab combines product engineering, applied AI research, governance design,
              and integration delivery. This lets us move fast without sacrificing enterprise
              reliability.
            </p>

            <div className="mt-8 grid sm:grid-cols-2 gap-3">
              {labCapacity.map((item, index) => {
                const Icon = capacityIcons[index % capacityIcons.length];

                return (
                  <div
                    key={item.title}
                    className="rounded-xl border border-[--border] bg-[--bg-secondary]/60 p-4"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <Icon className="w-4 h-4 text-[--accent]" />
                      <p className="text-sm font-semibold text-[--text-primary]">{item.title}</p>
                    </div>
                    <p className="text-sm text-[--text-secondary] leading-relaxed">{item.detail}</p>
                  </div>
                );
              })}
            </div>
          </div>

          <aside
            className={`soft-card rounded-3xl p-8 md:p-10 transition-all duration-700 ease-out delay-250 ${
              isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}
          >
            <p className="section-eyebrow mb-5">Operational Scale</p>

            <div className="space-y-4">
              {labStats.map((stat) => (
                <div key={stat.label} className="rounded-xl border border-[--border] bg-[--bg-secondary]/60 p-4">
                  <p className="text-[1.5rem] font-semibold text-[--text-primary] leading-none">{stat.value}</p>
                  <p className="mt-1 text-sm text-[--text-secondary]">{stat.label}</p>
                </div>
              ))}
            </div>

            <button
              onClick={openDemoModal}
              className="mt-7 w-full px-6 py-3 text-[11px] font-medium text-[--bg-primary] uppercase tracking-[0.18em] font-mono bg-[--accent] rounded-full hover:bg-[--accent-hover] transition-colors inline-flex items-center justify-center gap-2 btn-pattern"
            >
              Build with Synentia Lab
              <ArrowRight className="w-4 h-4" />
            </button>
          </aside>
        </div>
      </div>
    </section>
  );
};

export default Lab;
